import {
    ApolloLink,
    Observable,
    type FetchResult,
    type NextLink,
    type Operation,
} from '@apollo/client';
import {
    isQueryOperation,
    isSubscriptionOperation,
} from '@apollo/client/utilities';
import type { ObservableSubscription } from '@apollo/client/utilities/observables/Observable';

export const RATE_LIMIT_TOAST_ID = 'graphql-rate-limit';
export const RATE_LIMIT_BLOCKED_ERROR_NAME = 'RateLimitBlockedError';
const MAX_TIMER_DELAY_MS = 2_147_483_647;

type ToastHandler = (retryAt: number) => void;
interface Observer {
    next: (value: FetchResult) => void;
    error: (error: unknown) => void;
    complete: () => void;
}
interface QueryAttempt {
    operation: Operation;
    forward: NextLink;
    observer: Observer;
    retries: number;
    cancelled: boolean;
    timer?: number;
    subscription?: ObservableSubscription;
}

let cooldownUntil = 0;
let toastHandler: ToastHandler | undefined;
let toastVisible = false;
const handledRateLimitErrors = new WeakSet<object>();

export function resetRateLimitCoordinator(): void {
    cooldownUntil = 0;
    toastHandler = undefined;
    toastVisible = false;
}

export function setRateLimitToastHandler(handler: ToastHandler): () => void {
    toastHandler = handler;
    return () => {
        if (toastHandler === handler) toastHandler = undefined;
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function hasGetHeader(
    value: unknown,
): value is { get: (name: string) => unknown } {
    return isRecord(value) && typeof value.get === 'function';
}

function parseRetryAfter(value: string): number | undefined {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
        const seconds = Number(trimmed);
        return Number.isSafeInteger(seconds) && seconds > 0
            ? seconds * 1000
            : undefined;
    }
    if (trimmed.length !== 29) return undefined;
    const timestamp = Date.parse(trimmed);
    if (!Number.isFinite(timestamp) || timestamp <= Date.now())
        return undefined;
    return new Date(timestamp).toUTCString() === trimmed
        ? timestamp - Date.now()
        : undefined;
}

function getRateLimitDelay(error: unknown): number | undefined {
    if (
        !isRecord(error) ||
        error.statusCode !== 429 ||
        !isRecord(error.response)
    )
        return undefined;
    const headers = error.response.headers;
    if (!hasGetHeader(headers)) return undefined;
    const retryAfter = headers.get('Retry-After');
    if (typeof retryAfter !== 'string') return undefined;
    return parseRetryAfter(retryAfter);
}

function setCooldown(delay: number, operationIsQuery: boolean): void {
    cooldownUntil = Math.max(cooldownUntil, Date.now() + delay);
    if (!operationIsQuery || toastVisible) notifyToast(cooldownUntil);
}

function notifyToast(retryAt: number): void {
    if (retryAt <= Date.now()) {
        toastVisible = false;
        return;
    }
    toastVisible = true;
    toastHandler?.(retryAt);
}

export function clearRateLimitToast(): void {
    toastVisible = false;
}

export class RateLimitBlockedError extends Error {
    readonly statusCode = 429;
    readonly retryAt: number;

    constructor(retryAt: number) {
        super('Request blocked during rate-limit cooldown');
        this.name = RATE_LIMIT_BLOCKED_ERROR_NAME;
        this.retryAt = retryAt;
    }
}

export function isHandledRateLimitError(error: unknown): boolean {
    if (!isRecord(error)) return false;
    if (
        error.name === RATE_LIMIT_BLOCKED_ERROR_NAME ||
        handledRateLimitErrors.has(error)
    )
        return true;
    const networkError = error.networkError;
    return networkError !== error && isHandledRateLimitError(networkError);
}

function markRateLimitErrorHandled(error: unknown): void {
    if (isRecord(error)) handledRateLimitErrors.add(error);
}

function scheduleQuery(query: QueryAttempt): void {
    if (query.cancelled) return;
    const wait = cooldownUntil - Date.now();
    if (wait > 0) {
        query.timer = window.setTimeout(
            () => {
                query.timer = undefined;
                scheduleQuery(query);
            },
            Math.min(wait, MAX_TIMER_DELAY_MS),
        );
        return;
    }
    query.subscription = query.forward(query.operation).subscribe({
        next: (result) => {
            query.observer.next(result);
        },
        complete: () => {
            query.observer.complete();
        },
        error: (error: unknown) => {
            const delay = getRateLimitDelay(error);
            if (delay === undefined) {
                query.observer.error(error);
                return;
            }
            setCooldown(delay, true);
            markRateLimitErrorHandled(error);
            if (query.retries >= 1) {
                query.observer.error(error);
                return;
            }
            query.retries += 1;
            scheduleQuery(query);
        },
    });
}

const rateLimitLink = new ApolloLink((operation, forward) => {
    if (isSubscriptionOperation(operation.query)) return forward(operation);
    if (!isQueryOperation(operation.query)) {
        return new Observable((observer) => {
            if (cooldownUntil > Date.now()) {
                notifyToast(cooldownUntil);
                observer.error(new RateLimitBlockedError(cooldownUntil));
                return undefined;
            }
            const subscription = forward(operation).subscribe({
                next: (result) => {
                    observer.next(result);
                },
                complete: () => {
                    observer.complete();
                },
                error: (error: unknown) => {
                    const delay = getRateLimitDelay(error);
                    if (delay !== undefined) {
                        setCooldown(delay, false);
                        markRateLimitErrorHandled(error);
                    }
                    observer.error(error);
                },
            });
            return () => {
                subscription.unsubscribe();
            };
        });
    }
    return new Observable((observer) => {
        const query: QueryAttempt = {
            operation,
            forward,
            observer,
            retries: 0,
            cancelled: false,
        };
        scheduleQuery(query);
        return () => {
            query.cancelled = true;
            if (query.timer !== undefined) window.clearTimeout(query.timer);
            query.subscription?.unsubscribe();
        };
    });
});

export default rateLimitLink;
